import { metadata } from './../../app/page';
import { z } from 'zod';

import { logger } from '@/lib/logger';
import { ICoupon } from '@/lib/schemas/Coupon.zod';
import { ICustomer } from '@/lib/schemas/Customer.zod';
import { ISale } from '@/lib/schemas/Sale.zod';
import { IShippingProvider } from '@/lib/schemas/ShippingProvider.zod';

import { isServer } from '../env.service';
import { ApiResponse } from '../http/BaseHttpClient';
import { CheckoutData, CheckoutDataSchema, CheckoutOrder, IAddress, ICart, ICartEnriched, IOrder, IPaymentMethod } from '../types';
import { CHECKOUT_KEYS as APP_CHECKOUT_KEYS, ICheckoutShippingStorage } from '@/types/checkout';
import { CART_LOCAL_STORAGE_KEY, CART_PAYMENT_STATUS, CART_SHIPPING_STATUS, CART_STATUS, CHECKOUT_KEYS } from './../types/enum';
import { AbstractService } from './AbstractService';

/**
 * Fluent builder that collects checkout data then executes the purchase.
 */
export class Checkout {
  private data: Partial<{
    pay_method: Partial<IPaymentMethod>;
    shipping: {
      method?: Partial<IShippingProvider>;
      destination?: Partial<IAddress>;
    };
    shipping_provider_id?: number | null;
    orders: CheckoutOrder[];
    customer?: Partial<ICustomer>;
    coupons?: ICoupon[] | null | undefined;
    sales?: ISale[] | null | undefined;
    currency?: string | null | undefined;
    options?: any;
  }> = {
    orders: [],
    coupons: [],
    sales: [],
    currency: 'ARS',
    options: {},
  };

  private coerceNumber(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') return undefined;
    const n = Number(value);
    return Number.isNaN(n) ? undefined : n;
  }

  withOrders(orders: CheckoutOrder[]): this {
    this.data.orders = orders.map((order) => ({
      ...order,
      quantity: this.coerceNumber(order.quantity) ?? 0,
      product: {
        ...order.product,
        id: this.coerceNumber(order.product?.id),
      },
      variant: order.variant
        ? {
            ...order.variant,
            id: this.coerceNumber(order.variant.id),
          }
        : undefined,
    }));
    return this;
  }

  addOrder(order: CheckoutOrder): this {
    const normalized = {
      ...order,
      quantity: this.coerceNumber(order.quantity) ?? 0,
      product: {
        ...order.product,
        id: this.coerceNumber(order.product?.id),
      },
      variant: order.variant
        ? {
            ...order.variant,
            id: this.coerceNumber(order.variant.id),
          }
        : undefined,
    };

    this.data.orders = [...(this.data.orders ?? []), normalized];
    return this;
  }

  withPayMethod(payMethod: IPaymentMethod): this {
    this.data.pay_method = payMethod;
    return this;
  }

  withCustomer(customer: ICustomer): this {
    this.data.customer = customer;
    return this;
  }

  withShippingMethod(method: unknown): this {
    const raw = method as any;
    const id = this.coerceNumber(raw?.id);
    const normalized = { ...raw, id };

    // If the method id cannot be coerced, omit it (schema will handle optionality).
    if (id === undefined) {
      delete (normalized as any).id;
    }

    this.data.shipping = { ...this.data.shipping, method: normalized };
    this.data.shipping_provider_id = id;
    return this;
  }

  withShippingProviderId(id: number | string): this {
    const coerced = this.coerceNumber(id);
    this.data.shipping_provider_id = coerced;
    return this;
  }

  withShippingDestination(destination: unknown): this {
    this.data.shipping = { ...this.data.shipping, destination: destination as any };
    return this;
  }

  withCoupons(coupons: ICoupon[]): this {
    this.data.coupons = coupons;
    return this;
  }

  withCoupon(coupon: ICoupon): this {
    this.data.coupons = [...(this.data.coupons ?? []), coupon];
    return this;
  }

  withSales(sales: CheckoutData['sales']): this {
    this.data.sales = sales;
    return this;
  }

  withCurrency(currency: string): this {
    this.data.currency = currency;
    return this;
  }

  withOptions(options: unknown): this {
    this.data.options = options;
    return this;
  }

  validate(): { success: true; data: CheckoutData } | { success: false; errors: z.ZodError } {
    const result = CheckoutDataSchema.safeParse(this.data);
    if (!result.success) {
      return { success: false, errors: result.error };
    }
    return { success: true, data: result.data };
  }

  async build() {
    const validation = this.validate();
    if (!validation.success) {
      throw new Error(`Checkout validation failed: ${validation.errors.message}`);
    }
    return validation.data;
  }
}

/**
 * Cart service providing cart-related operations.
 */
export class CartService extends AbstractService {
  // client ------

  async clearLocalCart(): Promise<void> {
    if (isServer()) return; // No local cart on server side
    try {
      sessionStorage.removeItem(CART_LOCAL_STORAGE_KEY.CURRENT_CART);
      sessionStorage.removeItem(CART_LOCAL_STORAGE_KEY.CHECKOUT_DATA);
      this.clearLocalCartItems();
      logger.debug({ msg: 'CartService: clearLocalCart' });
    } catch (err) {
      logger.error({ msg: 'CartService: clearLocalCart error', error: err });
    }
  }

  async saveCartToLocal(cart: ICart): Promise<void> {
    if (isServer()) return; // No local cart on server side
    try {
      sessionStorage.setItem(CART_LOCAL_STORAGE_KEY.CURRENT_CART, JSON.stringify(cart));
      logger.debug({ msg: 'CartService: saveCartToLocal', cartId: cart.id });
    } catch (err) {
      logger.error({ msg: 'CartService: saveCartToLocal error', error: err });
    }
  }

  /**
   * Returns true when the product has at least one variant dimension (variants
   * array, colors, or sizes). Such products MUST have a variant object on the
   * order before it can be added to the cart.
   */
  private productRequiresVariant(product: IOrder['product']): boolean {
    const p = product as any;
    return !!(
      p?.variants?.length ||
      p?.colors?.length ||
      p?.sizes?.length
    );
  }

  /**
   * Returns a stable string key for a variant that survives round-trips through
   * sessionStorage. Priority:
   *   1. variant.id — the canonical backend id
   *   2. "color:size" composite — for synthetic variants built from product.colors × product.sizes
   *   3. undefined — product has no variant dimension
   */
  private variantKey(variant: IOrder['variant']): string | undefined {
    if (!variant) return undefined;
    if (variant.id != null && variant.id !== undefined) return String(variant.id);
    const colorName = (variant as any).color?.name;
    const sizeName = (variant as any).size?.name;
    if (colorName && sizeName) return `${colorName}:${sizeName}`;
    return undefined;
  }

  async addToCart(order: IOrder): Promise<void> {
    // Guard: products that have variant dimensions require a variant selection.
    if (this.productRequiresVariant(order.product) && !this.variantKey(order.variant)) {
      const err = new Error('Cannot add product to cart: a variant (color/size) must be selected before adding to cart.');
      logger.error({ msg: 'CartService: addToCart blocked — missing variant', productId: order.product?.id });
      throw err;
    }

    // Persist local cart item so anonymous users can keep items between page reloads.
    // For logged-in users, the checkout flow is responsible for syncing to the backend.
    // Accumulate quantity when the same product+variant is already in the cart.
    const incomingVariantKey = this.variantKey(order.variant);
    const existing = this.getLocalCartItems().find(
      (i) =>
        String(i.product?.id) === String(order.product?.id) &&
        this.variantKey(i.variant) === incomingVariantKey,
    );
    const merged = existing
      ? { ...order, quantity: (Number(existing.quantity) || 0) + (Number(order.quantity) || 1) }
      : order;
    this.saveLocalCartItem(merged);
  }

  private dispatchCartUpdated(): void {
    if (isServer()) return;
    try {
      window.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (_) {}
  }

  /**
   * Return all locally stored cart items.
   */
  getLocalCartItems(): IOrder[] {
    if (isServer()) return [];

    const itemsByKey = new Map<string, IOrder>();
    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(CHECKOUT_KEYS.ORDER_PREFIX)) {
          const value = sessionStorage.getItem(key);
          if (value) {
            const item: IOrder = JSON.parse(value);
            const productId = item.product?.id;
            const vKey = this.variantKey(item.variant);
            const itemKey = `${productId ?? ''}-${vKey ?? ''}`;
            if (!itemsByKey.has(itemKey)) {
              itemsByKey.set(itemKey, item);
            }
          }
        }
      }
    } catch (err) {
      logger.error({ msg: 'CartService: getLocalCartItems error', error: err });
    }
    return Array.from(itemsByKey.values());
  }

  /**
   * Save a single cart item to sessionStorage.
   * Uses order-<productId> key convention.
   */
  saveLocalCartItem(order: IOrder): void {
    if (isServer()) return;
    try {
      const productId = order.product?.id;
      if (!productId) return;

      // Guard: silently reject any order that lacks variant data for a variant product.
      // This protects the hydration path (hydrateSessionFromServerCart) from persisting
      // server-side corrupt orders.
      if (this.productRequiresVariant(order.product) && !this.variantKey(order.variant)) {
        logger.error({
          msg: 'CartService: saveLocalCartItem blocked — product requires a variant but order has none',
          productId,
          variant: order.variant ?? null,
        });
        return;
      }

      const vKey = this.variantKey(order.variant);
      const key = `${CHECKOUT_KEYS.ORDER_PREFIX}${productId}${vKey ? `-${vKey}` : ''}`;
      sessionStorage.setItem(key, JSON.stringify(order));
      this.dispatchCartUpdated();
    } catch (err) {
      logger.error({ msg: 'CartService: saveLocalCartItem error', error: err });
    }
  }

  /**
   * Remove a single cart item from sessionStorage.
   */
  removeLocalCartItem(order: IOrder): void {
    if (isServer()) return;
    try {
      const productId = order.product?.id;
      if (!productId) return;
      const vKey = this.variantKey(order.variant);
      const key = `${CHECKOUT_KEYS.ORDER_PREFIX}${productId}${vKey ? `-${vKey}` : ''}`;
      sessionStorage.removeItem(key);
      this.dispatchCartUpdated();
    } catch (err) {
      logger.error({ msg: 'CartService: removeLocalCartItem error', error: err });
    }
  }

  /**
   * Remove all cart items stored in sessionStorage.
   */
  clearLocalCartItems(): void {
    if (isServer()) return;
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(CHECKOUT_KEYS.ORDER_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => sessionStorage.removeItem(key));
      this.dispatchCartUpdated();
    } catch (err) {
      logger.error({ msg: 'CartService: clearLocalCartItems error', error: err });
    }
  }

  /**
   * Hydrate sessionStorage from the enriched server cart on the first load of a
   * fresh session (i.e. when there are no locally stored order items yet).
   * Persists order items, payment method, and shipping selection so the /cart
   * page can immediately render the last summary.
   */
  hydrateSessionFromServerCart(cart: ICartEnriched): void {
    if (isServer()) return;
    // Guard: skip if the user already has items in this session
    if (this.getLocalCartItems().length > 0) return;
    try {
      // Persist each order as a local cart item
      for (const order of cart.orders ?? []) {
        if (order.variant === null && (order as any).product?.variants?.length > 0) {
          logger.warn({
            msg: 'CartService: hydrateSessionFromServerCart — order has null variant but product has variants; variant link may be lost on the server',
            productId: (order as any).product?.id,
            orderId: (order as any).id,
          });
        }
        this.saveLocalCartItem(order);
      }
      // Persist payment method selection
      if (cart.pay_method) {
        const methodId = cart.pay_method.code ?? String(cart.pay_method.id ?? '');
        if (methodId) {
          sessionStorage.setItem(APP_CHECKOUT_KEYS.PAYMENT, JSON.stringify({ methodId }));
        }
      }
      // Persist shipping selection
      if (cart.shipping_provider_id) {
        const shippingData: Record<string, unknown> = { methodId: cart.shipping_provider_id };
        const destAddr = cart.destination_address as Record<string, unknown> | undefined;
        const isBranchAddr =
          destAddr?.address_type === 'BRANCH' || destAddr?.address_type === 'SUCURSAL';
        if (cart.destination_address_id != null) {
          if (isBranchAddr) {
            // TAKEAWAY: restore the branch as a location so the location selector can
            // display the pre-selected pickup point on resume.
            shippingData.location = destAddr;
          } else {
            // DELIVERY: store the DB-assigned address id for checkout linking.
            shippingData.addressId = cart.destination_address_id;
          }
        }
        if (destAddr && !isBranchAddr) {
          shippingData.address = destAddr;
        }
        sessionStorage.setItem(APP_CHECKOUT_KEYS.SHIPPING, JSON.stringify(shippingData));
      }
      logger.debug({ msg: 'CartService: hydrateSessionFromServerCart', cartId: cart.id });
    } catch (err) {
      logger.error({ msg: 'CartService: hydrateSessionFromServerCart error', error: err });
    }
  }

  // server / client API calls --------------

  async getCart(user?: any): Promise<ICartEnriched | undefined> {
    logger.debug({ msg: 'CartService: getCart' });

    const cart = await this.post<ICartEnriched>(this.endpoint('cartCurrent'), { ...user }, { timeout: 10000 });
    if (cart) {
      this.saveCartToLocal(cart);
      this.hydrateSessionFromServerCart(cart);
    }
    return cart;
  }

  /**
   * Create a new cart using the new `/shop/cart` endpoint.
   * Returns the created cart (including its id).
   */
  async createCart(customerId: string, checkoutData: Checkout | Record<string, unknown>): Promise<any> {
    logger.debug({ msg: 'CartService: createCart', customerId, checkoutData });

    let data: any;
    if (checkoutData instanceof Checkout) {
      const validation = checkoutData.validate();
      if (!validation.success) {
        logger.error({ msg: 'CartService: createCart validation failed', errors: validation.errors });
        throw new Error(`Checkout data validation failed: ${validation.errors.message}`);
      }
      data = await checkoutData.build();
    } else {
      data = checkoutData;
    }

    const payload = {
      customer_id: customerId,
      ...data,
    };

    const response = await this.post(this.endpoint('cartCreate'), payload, { timeout: 50000 });
    const cart = (response as any)?.data ?? response;
    if (cart) this.saveCartToLocal(cart);
    return cart;
  }

  async createCustomer(customerData: Partial<ICustomer>): Promise<any> {
    logger.debug({ msg: 'CartService: createCustomer', customerData });
    const response = await this.post(this.endpoint('shopCreateCustomer'), customerData, { timeout: 10000 });
    return (response as any)?.data ?? response;
  }

  async getProfile(email: string): Promise<any> {
    logger.debug({ msg: 'CartService: getProfile', email });
    const response = await this.get(this.endpoint('shopCustomerProfile', { email }), undefined, { timeout: 10000 });
    return (response as any)?.data ?? response;
  }

  /**
   * Fetch the last open cart for a given customer.
   */
  async getCartByCustomer(customerId: string): Promise<any> {
    logger.debug({ msg: 'CartService: getCartByCustomer', customerId });
    const response = await this.get(this.endpoint('cartByCustomer', { customerId }), undefined, { timeout: 10000 });
    const cart = (response as any)?.data ?? response;
    if (cart) this.saveCartToLocal(cart);
    return cart;
  }

  /**
   * Update an existing open cart for a customer (orders, shipping, etc.).
   * Uses the same payload shape as checkout.
   */
  async updateCartForCustomer(customerId: string, checkoutData: Checkout | Record<string, unknown>): Promise<any> {
    logger.debug({ msg: 'CartService: updateCartForCustomer', customerId, checkoutData });

    let data: any;
    if (checkoutData instanceof Checkout) {
      const validation = checkoutData.validate();
      if (!validation.success) {
        logger.error({ msg: 'CartService: updateCartForCustomer validation failed', errors: validation.errors });
        throw new Error(`Checkout data validation failed: ${validation.errors.message}`);
      }
      data = await checkoutData.build();
    } else {
      data = checkoutData;
    }

    const payload = {
      customer_id: customerId,
      ...data,
    };

    const response = await this.patch(this.endpoint('cartUpdate', { customerId }), payload, { timeout: 50000 });
    const cart = (response as any)?.data ?? response;
    if (cart) this.saveCartToLocal(cart);
    this.dispatchCartUpdated();
    return cart;
  }

  /**
   * Abandon the current open cart for the customer and create a new pending one.
   */
  async resetCartForCustomer(customerId: string): Promise<any> {
    logger.debug({ msg: 'CartService: resetCartForCustomer', customerId });
    const response = await this.delete(this.endpoint('cartReset', { customerId }));
    const cart = (response as any)?.data ?? response;
    if (cart) this.saveCartToLocal(cart);
    this.dispatchCartUpdated();
    return cart;
  }

  async updateCart(cartId: string, payload: Record<string, unknown>): Promise<any> {
    logger.debug({ msg: 'CartService: updateCart', cartId, payload });
    const response = await this.patch(this.endpoint('cartById', { cartId }), payload, { timeout: 10000 });
    const cart = (response as any)?.data ?? response;
    if (cart) this.saveCartToLocal(cart);
    this.dispatchCartUpdated();
    return cart;
  }

  /**
   * Update only the shipping provider on an existing cart.
   * Call this whenever the customer changes their shipping selection.
   */
  async updateShippingProvider(cartId: string, providerId: number | string): Promise<any> {
    const id = Number(providerId);
    if (Number.isNaN(id)) throw new Error('Invalid shipping provider id');
    logger.debug({ msg: 'CartService: updateShippingProvider', cartId, providerId: id });
    return this.updateCart(cartId, { shipping_provider: { id } });
  }

  /**
   * Patch the cart's destination address as soon as the customer makes a selection.
   * - TAKEAWAY: `{ type: 'TAKEAWAY', locationId }` → destination_address_id = locationId
   * - DELIVERY with saved backend id: `{ type: 'DELIVERY', addressId }` → destination_address_id = addressId
   * - DELIVERY with raw form data: `{ type: 'DELIVERY', address }` → destination_address = address
   */
  async updateDestinationAddress(
    cartId: string,
    selection:
      | { type: 'TAKEAWAY'; locationId: number | string }
      | { type: 'DELIVERY'; addressId: number | string }
      | { type: 'DELIVERY'; address: Record<string, unknown> },
  ): Promise<any> {
    if (!cartId) throw new Error('Cart id is required for updateDestinationAddress');

    const patch: Record<string, unknown> = {};

    if (selection.type === 'TAKEAWAY') {
      patch.destination_address = { id: (selection as { type: 'TAKEAWAY'; locationId: number | string }).locationId };
    } else if ('addressId' in selection) {
      patch.destination_address = { id: selection.addressId };
    } else {
      patch.destination_address = (selection as { type: 'DELIVERY'; address: Record<string, unknown> }).address;
    }

    logger.debug({ msg: 'CartService: updateDestinationAddress', cartId, selection });
    return this.updateCart(cartId, patch);
  }

  async clearCart(cartId: string): Promise<ApiResponse<any>> {
    logger.debug({ msg: 'CartService: clearCart', cartId });
    const payload = {
      status: CART_STATUS.ABANDONED,
      payment_status: CART_PAYMENT_STATUS.FAILED,
      shipping_status: CART_SHIPPING_STATUS.CANCELLED,
    };
    this.clearLocalCart();
    return this.updateCart(cartId, payload);
  }

  // checkout related handlers

  async checkoutCart<T>(checkoutData: Checkout): Promise<T> {
    logger.warn({ msg: 'CartService: checkoutCart', checkoutData });
    const validation = checkoutData.validate();

    if (!validation.success) {
      logger.error({ msg: 'CartService: checkoutCart validation failed', errors: validation.errors });
      throw new Error(`Checkout data validation failed: ${validation.errors.message}`);
    }

    const payload = {
      ...(await checkoutData.build()),
    };

    const paymethodId = checkoutData['data']?.pay_method?.id;
    if (!paymethodId) {
      logger.error({ msg: 'CartService: checkoutCart missing payment method', checkoutData });
      throw new Error('Payment method is required for checkout');
    }

    const response = await this.post(this.endpoint('checkoutCartByPayMethod', { paymethodId: paymethodId }), payload, { timeout: 50000 });

    const data = (response as any) || {};
    const cartId = (data as any)?.metadata?.cart_id ?? (data as any)?.cart?.id ?? (data as any)?.cartId ?? (data as any)?.id;
    const customerId = (data as any)?.metadata?.cart_id ?? (data as any)?.cart?.customer_id ?? (data as any)?.customerId ?? (data as any)?.customer?.id;


    return { ...data, cartId, customerId } as T;
  }

  /**
   * Checkout using an existing cart id (avoids re-sending full payload).
   */
  async checkoutExistingCart<T>(cartId: string, paymethodId: string | number): Promise<T> {
    logger.debug({ msg: 'CartService: checkoutExistingCart', cartId, paymethodId });

    if (!cartId) throw new Error('Cart id is required for checkoutExistingCart');
    if (!paymethodId) throw new Error('Payment method id is required for checkoutExistingCart');

    const actual_orders = this.getLocalCartItems().filter(
      (o) => o.product?.id != null && Number(o.quantity) > 0,
    );

    // Read the last saved shipping selection so the most recent customer
    // choice is always sent to checkout.
    let shipping_provider_id: number | undefined;
    let destination_address_id: number | string | undefined;
    let destination_address: Record<string, unknown> | undefined;

    if (!isServer()) {
      try {
        const raw = sessionStorage.getItem(APP_CHECKOUT_KEYS.SHIPPING);
        if (raw) {
          const saved = JSON.parse(raw) as ICheckoutShippingStorage;

          const parsed = Number(saved?.methodId);
          if (!Number.isNaN(parsed) && parsed > 0) shipping_provider_id = parsed;

          // DELIVERY: prefer the backend-assigned addressId; fall back to inline object.
          // TAKEAWAY location.id is intentionally NOT forwarded here: it may be an external
          // provider ID (e.g. OCA's IdCentroImposicion) that does not correspond to a DB
          // Address row. Destination address is not required for TAKEAWAY checkout.
          if (saved?.addressId != null) {
            destination_address_id = saved.addressId;
          } else if (saved?.address) {
            // strip null/undefined/empty-string fields before sending
            destination_address = Object.fromEntries(
              Object.entries(saved.address as Record<string, unknown>).filter(
                ([, v]) => v !== null && v !== undefined && v !== '',
              ),
            );
          }
        }
      } catch (_) {
        // non-critical – proceed without it
      }
    }

    const payload: Record<string, unknown> = { orders: actual_orders };
    if (shipping_provider_id !== undefined) payload.shipping_provider_id = shipping_provider_id;
    if (destination_address_id !== undefined && destination_address_id !== null && destination_address_id !== '') payload.destination_address_id = destination_address_id;
    if (destination_address !== undefined && Object.keys(destination_address).length > 0) payload.destination_address = destination_address;

    // Include the customer id so the API can verify cart ownership before checkout.
    if (!isServer()) {
      try {
        const rawCustomer = sessionStorage.getItem(APP_CHECKOUT_KEYS.CUSTOMER);
        if (rawCustomer) {
          const customerInfo = JSON.parse(rawCustomer) as any;
          const customerId = customerInfo?.customerId;
          if (customerId != null) payload.customerId = Number(customerId);
        }
      } catch (_) {
        // non-critical – proceed without it; the API will reject if ownership cannot be verified
      }
    }

    logger.debug({ msg: 'CartService: checkoutExistingCart payload', payload });

    const response = await this.post(this.endpoint('checkoutCartByCartId', { cartId }), payload, { timeout: 50000 });
    const data = (response as any)?.data ?? response;

    const returnedCartId = (data as any)?.metadata?.cart_id ?? (data as any)?.cart?.id ?? (data as any)?.cartId ?? (data as any)?.id;
    const returnedCustomerId = (data as any)?.metadata?.customer_id ?? (data as any)?.cart?.customer_id ?? (data as any)?.customerId ?? (data as any)?.customer?.id;


    return { ...data, cartId: returnedCartId, customerId: returnedCustomerId } as T;
  }

  async onCheckoutSuccess(cartId: string, options: Record<string, unknown>): Promise<ApiResponse<any>> {
    if (!cartId) {
      logger.error({ msg: 'CartService: onCheckoutSuccess missing cartId', options });
      throw new Error('Cart id is required for onCheckoutSuccess');
    }

    logger.debug({ msg: 'CartService: onCheckoutSuccess', cartId, options });

    const INCOMING_PAYMENT_STATUS = ((options as any)?.status as string).toLowerCase();
    const PAYMENT_ID = (options as any)?.transactionId ?? (options as any)?.paymentId ?? (options as any)?.payment_id;

    const payment_status = ['approved', 'completed'].includes(INCOMING_PAYMENT_STATUS) ? CART_PAYMENT_STATUS.COMPLETED : CART_PAYMENT_STATUS.PENDING;
    const payload = { status: CART_STATUS.CONFIRMED, payment_status: payment_status };
    await this.updateCart(cartId, payload);
    try {
      if (!PAYMENT_ID) {
        logger.error({ msg: 'CartService: onCheckoutSuccess missing payment id in options', options });
        throw new Error('Payment id is required for payment confirmation');
      }
      const payload = { cartId, paymentId: PAYMENT_ID };
      logger.debug({ msg: 'CartService: confirming payment with payload', payload });
      // const response = await this.post(this.endpoint('shopPaymentVerify', { paymentId: PAYMENT_ID }), payload, { timeout: 10000 });
      // logger.info({ msg: 'CartService: confirmPayment response', response });
    } catch (err) {
      logger.warn({ msg: 'CartService: Payment Confirmation error', error: err });
    }
  }

  async onCheckoutFailure(cartId: string, options: Record<string, unknown>): Promise<ApiResponse<any>> {
    if (!cartId) {
      logger.error({ msg: 'CartService: onCheckoutFailure missing cartId', options });
      throw new Error('Cart id is required for onCheckoutFailure');
    }

    logger.debug({ msg: 'CartService: onCheckoutFailure', cartId, options });
    const payload = { status: CART_STATUS.CONFIRMED, payment_status: CART_PAYMENT_STATUS.FAILED };
    return this.updateCart(cartId, payload);
  }
}

export const cartService = new CartService();
export const clientCartService = () => cartService;
export const serverCartService = () => cartService;

export default CartService;
